/**
 * Email Notification Service
 * Centralized service for triggering email notifications throughout the application
 */

import { supabase } from '../utils/supabase';

/**
 * Send profile picture approval/rejection notification
 */
export async function sendProfilePictureNotification(userId, status, rejectionReason = null) {
  try {
    const { data, error } = await supabase.functions.invoke('send-profile-picture-notification', {
      body: {
        userId,
        status,
        rejectionReason
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error sending profile picture notification:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Send timesheet notification (submitted/approved/rejected)
 */
export async function sendTimesheetNotification(timesheetId, eventType) {
  try {
    const { data, error } = await supabase.functions.invoke('send-timesheet-notifications', {
      body: {
        timesheetId,
        eventType
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error sending timesheet notification:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Send security alert notification
 */
export async function sendSecurityAlert(userId, alertType, metadata) {
  try {
    const { data, error } = await supabase.functions.invoke('send-security-alert', {
      body: {
        userId,
        alertType,
        metadata
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error sending security alert:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Send interview update notification
 */
export async function sendInterviewUpdate(interviewId, eventType, metadata = {}) {
  try {
    const { data, error } = await supabase.functions.invoke('send-interview-update', {
      body: {
        interviewId,
        eventType,
        metadata
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error sending interview update:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Send account deletion confirmation
 */
export async function sendAccountDeletionConfirmation(userId, confirmationToken) {
  try {
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('email, full_name')
      .eq('id', userId)
      .single();

    if (!profile) throw new Error('User profile not found');

    const { data, error } = await supabase.functions.invoke('send-email', {
      body: {
        emailType: 'account-deletion-confirmation',
        email: profile.email,
        fullName: profile.full_name,
        confirmationUrl: `https://dashboard.vasprouts.com/confirm-deletion?token=${confirmationToken}`,
        expiresIn: 24
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error sending account deletion confirmation:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Send email change verification
 */
export async function sendEmailChangeVerification(userId, oldEmail, newEmail, verificationToken) {
  try {
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('full_name')
      .eq('id', userId)
      .single();

    if (!profile) throw new Error('User profile not found');

    // Send verification to new email
    const { error: verifyError } = await supabase.functions.invoke('send-email', {
      body: {
        emailType: 'email_change_verification',
        email: newEmail,
        currentEmail: oldEmail,
        fullName: profile.full_name,
        verificationUrl: `https://dashboard.vasprouts.com/verify-email?token=${verificationToken}`
      }
    });

    if (verifyError) throw verifyError;

    // Send security alert to old email
    const { error: alertError } = await supabase.functions.invoke('send-email', {
      body: {
        emailType: 'email-change-security-alert',
        email: oldEmail,
        fullName: profile.full_name,
        newEmail: newEmail,
        changeDate: new Date().toLocaleDateString(),
        securityUrl: 'https://dashboard.vasprouts.com/security/revert-email-change'
      }
    });

    if (alertError) throw alertError;

    return { success: true };
  } catch (error) {
    console.error('Error sending email change verification:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Send job posting status notification
 */
export async function sendJobStatusNotification(jobId, status) {
  try {
    const { data: job } = await supabase
      .from('jobs')
      .select(`
        id,
        title,
        created_at,
        expires_at,
        employer:employer_id(email, full_name)
      `)
      .eq('id', jobId)
      .single();

    if (!job) throw new Error('Job not found');

    let emailType;
    let additionalData = {};

    switch(status) {
      case 'active':
        emailType = 'job-posted-confirmation';
        additionalData = {
          postedDate: new Date(job.created_at).toLocaleDateString(),
          expiryDate: new Date(job.expires_at).toLocaleDateString()
        };
        break;
      case 'expired':
        emailType = 'job-expired-alert';
        additionalData = {
          expiredDate: new Date().toLocaleDateString(),
          renewalUrl: `https://dashboard.vasprouts.com/jobs/${jobId}/renew`
        };
        break;
      case 'filled':
        emailType = 'job-filled-notification';
        additionalData = {
          filledDate: new Date().toLocaleDateString()
        };
        break;
      default:
        throw new Error(`Unknown job status: ${status}`);
    }

    const { data, error } = await supabase.functions.invoke('send-email', {
      body: {
        emailType,
        email: job.employer.email,
        fullName: job.employer.full_name,
        jobTitle: job.title,
        jobUrl: `https://dashboard.vasprouts.com/jobs/${jobId}`,
        ...additionalData
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error sending job status notification:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Send feedback received notification
 */
export async function sendFeedbackNotification(feedbackId) {
  try {
    const { data: feedback } = await supabase
      .from('user_feedback')
      .select(`
        id,
        rating,
        comment,
        created_at,
        recipient:recipient_id(id, email, full_name),
        reviewer:reviewer_id(id, full_name)
      `)
      .eq('id', feedbackId)
      .single();

    if (!feedback) throw new Error('Feedback not found');

    const { data, error } = await supabase.functions.invoke('send-email', {
      body: {
        emailType: 'feedback-received',
        email: feedback.recipient.email,
        fullName: feedback.recipient.full_name,
        reviewerName: feedback.reviewer.full_name,
        rating: feedback.rating,
        comment: feedback.comment || '',
        reviewDate: new Date(feedback.created_at).toLocaleDateString(),
        profileUrl: 'https://dashboard.vasprouts.com/profile'
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error sending feedback notification:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Send admin-initiated account deletion notification
 * This notifies the user that their account was deleted by an administrator
 * Note: Email sending is optional for admin deletions since the user account is already deleted
 */
export async function sendAdminDeletionNotification(userEmail, userName, reason, adminNote) {
  try {
    // For admin-initiated deletions, we don't need to send email since the account is already deleted
    // and the user cannot access their email through the platform
    // Log the deletion action for the admin audit trail instead
    console.log(`[ADMIN DELETION] User: ${userName} (${userEmail}), Reason: ${reason}, Admin Note: ${adminNote}`);
    
    // Return success without attempting email (avoids Edge Function failures)
    return { success: true, skipped: true, message: 'Admin deletion logged (email skipped)' };
  } catch (error) {
    // Even if something goes wrong, return success since email is not critical for admin deletions
    console.warn('Warning in admin deletion notification:', error.message);
    return { success: true, skipped: true, warning: error.message };
  }
}

export default {
  sendProfilePictureNotification,
  sendTimesheetNotification,
  sendSecurityAlert,
  sendInterviewUpdate,
  sendAccountDeletionConfirmation,
  sendEmailChangeVerification,
  sendJobStatusNotification,
  sendFeedbackNotification,
  sendAdminDeletionNotification
};
